import _ from 'lodash';

import { getJson } from '@zapier/toolbox-browser-fetch';
import {
  EntityActions,
  buildEntityActions,
  buildEntityApi,
  buildEntitySelectors,
} from '@zapier/toolbox-redux-framework';
import { formatPlanTiers, isTierFree } from '@zapier/design-system-revenue';

import partialWithDefault from '../utils/partialWithDefault';
import emptyObject from '../utils/emptyObject';

import * as Account from './Account';
import * as Profile from './Profile';

export const type = 'plan';

export const api = buildEntityApi(
  type,
  (path, getState) => {
    const currentAccountId = Account.selectors.all.currentAccountId(getState());
    return `/api/v3/accounts/${currentAccountId}/plans/${path}`;
  },
  planApi => {
    planApi.loadPlansWithinPlanGroup = planGroupId =>
      getJson(`/api/v3/plan-groups/${planGroupId}/plans`);

    planApi.loadChangeDetailsForPlan = (planId, getState) =>
      getJson(`${planApi.modelEndpoint(planId, getState)}/change-details`);
  }
);

const LOADED_PLAN_GROUP_ALIAS = 'loadedPlanGroupAlias';

export const selectors = buildEntitySelectors(type, sel => {
  sel.amount = _.partial(sel.dataItem, 'amount');
  sel.amountPerMonth = entity => {
    const amount = sel.amount(entity) || 0;
    return sel.isAnnual(entity) ? amount / 12 : amount;
  };
  sel.hasTeams = _.partial(sel.dataItem, 'allow_teams');
  sel.hasCompanies = _.partial(sel.dataItem, 'allow_companies');
  sel.interval = _.partial(sel.dataItem, 'interval');
  sel.name = _.partial(sel.dataItem, 'name');
  sel.tiers = entity => formatPlanTiers(sel.data(entity));
  sel.maxMeteringCharge = _.partial(sel.dataItem, 'max_metering_charge');
  sel.isMetered = entity => !_.isEmpty(sel.tiers(entity));
  sel.isFree = entity => sel.amount(entity) === 0;
  sel.isAnnual = _.partial(sel.matchesSelector, sel.interval, 'year');
  sel.isMonthly = _.partial(sel.matchesSelector, sel.interval, 'month');
  sel.canAutoreplay = _.partial(sel.dataItem, 'can_autoreplay_errored_tasks');
  sel.type = _.partial(sel.dataItem, 'plan_type');
  sel.displayName = _.partial(sel.dataItem, 'short_name');
  sel.descriptiveName = _.partial(sel.dataItem, 'descriptive_name');
  sel.allowDataRetentionConfig = partialWithDefault(
    sel.dataItem,
    ['allow_data_retention_config'],
    false
  );
  sel.isCompaniesPlan = partialWithDefault(
    sel.dataItem,
    ['allow_companies'],
    false
  );
  sel.changeDetails = partialWithDefault(
    sel.dataItem,
    ['change_details'],
    emptyObject
  );
  sel.meteredPricingTiers = partialWithDefault(
    sel.dataItem,
    ['metered_pricing_tiers'],
    undefined
  );
  sel.zapIdsThatWouldPauseByChanging = entity =>
    _.flow(
      sel.changeDetails,
      changeDetails => _.get(changeDetails, 'zap_ids_to_pause', {}),
      Object.keys,
      ids => ids.filter(Boolean).map(id => parseInt(id, 10))
    )(entity);

  sel.numFreeMeteredTasks = entity => {
    const tiers = sel.tiers(entity);
    const freeTier = _.find(tiers, isTierFree);
    return freeTier ? freeTier.numTasks : 0;
  };

  sel.canUsePaths = partialWithDefault(
    sel.dataItem,
    ['allow_branching_zaps'],
    false
  );

  sel.all.loadedPlanGroupAlias = _.partial(
    sel.all.stateItem,
    LOADED_PLAN_GROUP_ALIAS
  );
  sel.all.defaultId = _.partial(sel.all.dataItem, 'default_id');
  sel.all.findDefaultPlan = state =>
    sel.all.entity(sel.all.defaultId(state), state);
  sel.all.preferredId = _.partial(sel.all.dataItem, 'preferred_id');

  /**
   * Finds the plan that is one tier higher than the current plan.
   * Useful for determining what's the next "logical" upgrade for the current user.
   * For example, if the user is on the "Starter" plan, this selector returns the "Professional" plan.
   * The order is determined by the pricing of the plans.
   */
  sel.all.nextPlanTierBy = (interval, state) => {
    const currentPlan = sel.all.currentPlan(state);
    const searchedInterval = interval || sel.interval(currentPlan);

    const plans = sel.all
      .entities(state)
      .filter(
        p =>
          sel.interval(p) === searchedInterval &&
          sel.amount(p) > sel.amount(currentPlan)
      );

    const sortedPlans = _.sortBy(plans, p => sel.amount(p));

    return sortedPlans[0];
  };

  sel.all.nextPlanTier = _.partial(sel.all.nextPlanTierBy, null);
  sel.all.nextPlanTierByYear = _.partial(sel.all.nextPlanTierBy, 'year');
  sel.all.nextPlanTierByMonth = _.partial(sel.all.nextPlanTierBy, 'month');

  sel.all.findPreferredPlan = state =>
    sel.all.entity(sel.all.preferredId(state), state);

  sel.all.findTeamPlan = _.partial(
    sel.all.findBy,
    plan => sel.hasTeams(plan) && !sel.isCompaniesPlan(plan)
  );
  sel.all.findMonthlyTeamPlan = _.partial(
    sel.all.findBy,
    plan =>
      sel.hasTeams(plan) && !sel.isCompaniesPlan(plan) && sel.isMonthly(plan)
  );
  sel.all.findAnnualTeamPlan = _.partial(
    sel.all.findBy,
    plan =>
      sel.hasTeams(plan) && !sel.isCompaniesPlan(plan) && sel.isAnnual(plan)
  );

  sel.all.findCompanyPlan = _.partial(sel.all.findBy, plan =>
    sel.isCompaniesPlan(plan)
  );

  sel.all.currentPlan = state =>
    sel.all.entity(Account.selectors.all.currentPlanId(state), state);

  sel.all.isCurrentPlanFree = state => sel.isFree(sel.all.currentPlan(state));

  // Plans are basically static so once we have them, no need
  // to reload them unless we don't have the `currentPlan`
  sel.all.shouldLoad = state =>
    !sel.all.isLoading(state) && !sel.all.currentPlan(state);
});

export const actions = buildEntityActions(
  type,
  selectors,
  api,
  entityActions => {
    const { loadCollection } = entityActions;

    entityActions.loadCollection = () => {
      return async (dispatch, getState) => {
        const state = getState();
        const currentAccountPlanGroupAlias = 'current-account';
        const loadedPlanGroupAlias = selectors.all.loadedPlanGroupAlias(state);

        // Plans are dependent on an account.
        await dispatch(Account.actions.loadCollection());

        if (!Account.selectors.all.currentAccountId(getState())) {
          return null;
        }

        // It's possible we've loaded plans from another plan group and
        // if that's the case then we need to discard those plans and load
        // the current account's plans.
        return dispatch(
          loadedPlanGroupAlias &&
            loadedPlanGroupAlias !== currentAccountPlanGroupAlias
            ? entityActions.resetAndLoadCollection()
            : loadCollection()
        ).then(() => {
          dispatch(
            entityActions.setLoadedPlanGroupAlias(currentAccountPlanGroupAlias)
          );
        });
      };
    };

    entityActions.loadPlansWithinPlanGroup = (planGroupId, messages) => {
      return dispatch => {
        return dispatch(
          EntityActions.resetAndLoadCollection(
            type,
            () => api.loadPlansWithinPlanGroup(planGroupId),
            () => true,
            selectors.all.isLoading,
            messages
          )
        ).then(() => {
          dispatch(entityActions.setLoadedPlanGroupAlias(planGroupId));
        });
      };
    };

    entityActions.loadChangeDetailsForPlan = (messages, planId) => {
      return EntityActions.loadEntity(
        type,
        api.loadChangeDetailsForPlan,
        messages,
        planId
      );
    };

    entityActions.loadDefaultPlans = _.partial(
      entityActions.loadPlansWithinPlanGroup,
      'default'
    );

    entityActions.loadOwnOrDefaultPlans = () => (dispatch, getState) => {
      const state = getState();
      const currentAccountId = Account.selectors.all.currentAccountId(state);
      const isAdmin = Profile.selectors.all.isAdminOf(currentAccountId, state);
      return isAdmin
        ? dispatch(entityActions.loadCollection())
        : dispatch(entityActions.loadDefaultPlans());
    };

    entityActions.setLoadedPlanGroupAlias = _.partial(
      entityActions.updateCollectionStateItem,
      LOADED_PLAN_GROUP_ALIAS
    );
  }
);
